import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Cpu, Download, HardDrive, Loader2, MemoryStick, Play, RefreshCw, Square, Trash2, X,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog';

import { Switch } from '@/components/ui/switch';
import {
  modelEndpointsApi as api,
  ModelEndpointError,
  TERMINAL_JOB_STATES,
  type EndpointConfig,
  type EngineEnv,
  type JobSnapshot,
  type Resources,
} from '@/api/modelEndpoints';
import { subscribeJobEvents } from '@/lib/sse';
import { refreshAppConfig } from '@/hooks/useAppConfig';

/**
 * 模型管理頁（單機版限定;線上版由路由層 OnlyInSingleUserMode 擋下,本頁不會被掛載）。
 *
 * 設計：docs/MODEL_MANAGEMENT_INTEGRATION_PLAN.md（F2）。
 * - backend 分頁依 GET /capabilities（Windows 只有 ollama,vLLM 分頁自然不出現）
 * - 啟動模型 job 成功 → POST /api/llm-runtime/apply → 問答立即換模型並寫回 config.env
 * - 沒模型在運行（409）不是錯誤：問答時會依 config.env 自動載入
 */

const BACKEND_LABEL: Record<string, string> = {
  ollama: 'Ollama',
  vllm: 'vLLM',
};

const REASON_TEXT: Record<string, string> = {
  no_model_running: '目前沒有模型在運行',
  backend_unreachable: '推論引擎連不上（請確認 Ollama 服務已啟動）',
  insufficient_resources: '資源不足（記憶體/顯存/磁碟），已擋下操作',
  context_exceeds_limit: 'context 超過模型架構上限',
  action_in_progress: '已有相同模型的操作進行中',
  model_not_found: '找不到此模型',
  backend_not_supported: '此平台不支援該推論引擎',
  job_not_found: '工作不存在（可能已逾保留期）',
};

function errText(e: unknown): string {
  if (e instanceof ModelEndpointError && e.reason && REASON_TEXT[e.reason]) {
    return REASON_TEXT[e.reason];
  }
  return e instanceof Error ? e.message : String(e);
}

function fmtBytes(n?: number | null): string {
  if (n == null || !isFinite(n) || n <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v >= 10 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

function fmtEta(sec?: number | null): string {
  if (sec == null || !isFinite(sec) || sec <= 0) return '';
  if (sec < 60) return `約 ${Math.round(sec)} 秒`;
  return `約 ${Math.round(sec / 60)} 分鐘`;
}

/** 表列共用：從 ollama/vLLM 的清單項目取顯示名（兩後端欄位名不同）。 */
function entryName(m: Record<string, unknown>): string {
  return String(m.name ?? m.id ?? m.model ?? '');
}

interface TrackedJob {
  snapshot: JobSnapshot | null;
  kind: string;
  model: string;
}

export default function ModelManagementPage() {
  const [backends, setBackends] = useState<string[]>([]);
  const [backend, setBackend] = useState<string>('ollama');
  const [cfg, setCfg] = useState<EndpointConfig | null>(null);
  const [resources, setResources] = useState<Resources | null>(null);
  const [models, setModels] = useState<Record<string, unknown>[]>([]);
  const [running, setRunning] = useState<Record<string, unknown>[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [pullName, setPullName] = useState('');
  const [busyModel, setBusyModel] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [jobs, setJobs] = useState<Record<string, TrackedJob>>({});
  // 引擎加速開關（OLLAMA_VULKAN）：null = 尚未載入或不可用
  const [engineEnv, setEngineEnvState] = useState<EngineEnv | null>(null);
  const [engineSwitching, setEngineSwitching] = useState(false);
  const [confirmVulkan, setConfirmVulkan] = useState<boolean | null>(null);
  const [confirmDisable, setConfirmDisable] = useState(false);
  // SSE 訂閱與輪詢的清理句柄（unmount 時全關）
  const cleanups = useRef<Map<string, () => void>>(new Map());
  // 請求序號：晚到的舊回應（輪詢與動作後刷新交錯時）直接丟棄，不蓋掉新狀態
  const cfgSeq = useRef(0);
  const runningSeq = useRef(0);
  // 動作後的延遲補刷計時器（涵蓋 Ollama 非同步卸載）
  const delayedRefresh = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 目前分頁的即時參照：job 完成回呼與延遲補刷讀這個——job 可能跑數分鐘，
  // 完成時使用者可能已切到別的分頁，吃閉包裡的舊值會把錯誤後端的清單寫進畫面
  const backendRef = useRef(backend);
  useEffect(() => {
    backendRef.current = backend;
  }, [backend]);

  const refreshStatus = useCallback(async () => {
    const seq = ++cfgSeq.current;
    // 一律帶 refresh=1：本頁要的是引擎即時狀態（1.5s TTL 快取是問答主流程
    // 在用的）；全部同為「強制」也消除「較晚發出、命中舊快取的回應反而
    // 蓋掉較早強制刷新結果」的競態。兩請求互相獨立 → 並行、各自成敗。
    const [c, r] = await Promise.allSettled([api.config(true), api.resources()]);
    if (seq !== cfgSeq.current) return; // 已有更新的一輪在跑，整組放棄
    if (c.status === 'fulfilled') setCfg(c.value);
    if (r.status === 'fulfilled') setResources(r.value);
  }, []);

  /** 只刷新「運行中」清單（10s 輪詢與延遲補刷用）；失敗靜默，下一輪再試。 */
  const refreshRunning = useCallback(async (b: string) => {
    const seq = ++runningSeq.current;
    try {
      const r = await api.runningModels(b);
      if (seq === runningSeq.current) setRunning(r);
    } catch {
      /* 輪詢性質，靜默 */
    }
  }, []);

  const refreshModels = useCallback(async (b: string) => {
    setLoadingList(true);
    const seq = ++runningSeq.current;
    try {
      const [m, r] = await Promise.all([api.models(b), api.runningModels(b)]);
      // models 與 running 同組提交：只擋一半會出現「A 後端清單配 B 後端
      // 運行狀態」的混合畫面。loadingList 刻意不掛防護——共用計數器的
      // refreshRunning 不會清 spinner，掛了防護會讓它永遠轉下去。
      if (seq === runningSeq.current) {
        setModels(m);
        setRunning(r);
      }
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoadingList(false);
    }
  }, []);

  /** 動作完成後的刷新：立即刷一輪，預設 +2 秒再補一次——停止/引擎重啟後
   *  Ollama 的卸載是非同步的（後端 stop 已加收斂等待，此為保險）。
   *  一律刷「目前顯示中」的分頁（backendRef）。下載完成用 delayed:false：
   *  不需要卸載收斂，也不去動別的動作排下的收斂計時器。 */
  const refreshAfterAction = useCallback(
    (opts?: { delayed?: boolean }) => {
      void refreshStatus();
      void refreshModels(backendRef.current);
      if (opts?.delayed === false) return;
      if (delayedRefresh.current) clearTimeout(delayedRefresh.current);
      delayedRefresh.current = setTimeout(() => {
        delayedRefresh.current = null;
        void refreshStatus();
        void refreshRunning(backendRef.current);
      }, 2000);
    },
    [refreshStatus, refreshModels, refreshRunning],
  );

  // 初始化：engine-env（含總開關）→ capabilities → 預設分頁 → 首輪載入。
  // 總開關關閉時整頁停用，不打任何管理 API（使用者自管 llama.cpp 等地端服務）。
  useEffect(() => {
    let alive = true;
    (async () => {
      const e = await api.engineEnv().catch(() => null);
      if (!alive) return;
      if (e) setEngineEnvState(e);
      if (e && e.management_enabled === false) return; // 停用：不載入其餘
      try {
        const caps = await api.capabilities();
        if (!alive) return;
        setBackends(caps.supported_backends);
        const first = caps.supported_backends[0] ?? 'ollama';
        setBackend(first);
        void refreshStatus();
        void refreshModels(first);
      } catch (err) {
        toast.error(errText(err));
      }
    })();
    return () => {
      alive = false;
    };
  }, [refreshStatus, refreshModels]);

  // 10s 週期刷新（狀態與運行中;清單靠動作後刷新;總開關停用時不打）
  const managementDisabled = engineEnv?.management_enabled === false;
  useEffect(() => {
    if (managementDisabled) return;
    const t = setInterval(() => {
      void refreshStatus();
      void refreshRunning(backend);
    }, 10_000);
    return () => clearInterval(t);
  }, [backend, refreshStatus, refreshRunning, managementDisabled]);

  // unmount 清理所有 SSE/輪詢
  useEffect(
    () => () => {
      cleanups.current.forEach((fn) => fn());
      cleanups.current.clear();
      if (delayedRefresh.current) clearTimeout(delayedRefresh.current);
    },
    [],
  );

  /** 追蹤一個 job 直到終態（SSE 為主,斷線改輪詢收尾）。 */
  const trackJob = useCallback(
    (jobId: string, kind: string, model: string, onDone: (snap: JobSnapshot) => void) => {
      setJobs((prev) => ({ ...prev, [jobId]: { snapshot: null, kind, model } }));
      const finish = (snap: JobSnapshot) => {
        cleanups.current.get(jobId)?.();
        cleanups.current.delete(jobId);
        setJobs((prev) => {
          const next = { ...prev };
          delete next[jobId];
          return next;
        });
        onDone(snap);
      };
      const onSnap = (snap: JobSnapshot) => {
        if (TERMINAL_JOB_STATES.includes(snap.state)) {
          finish(snap);
        } else {
          setJobs((prev) => ({ ...prev, [jobId]: { snapshot: snap, kind, model } }));
        }
      };
      const sub = subscribeJobEvents(jobId, onSnap, () => {
        // SSE 斷線 → 輪詢收尾（後端保證 job 不因斷線中止）
        const t = setInterval(() => {
          api
            .job(jobId)
            .then(onSnap)
            .catch(() => finish({ state: 'failed', error: '工作狀態查詢失敗' } as JobSnapshot));
        }, 1500);
        cleanups.current.set(jobId, () => clearInterval(t));
      });
      cleanups.current.set(jobId, sub.close);
    },
    [],
  );

  const handlePull = async () => {
    const name = pullName.trim();
    if (!name) return;
    try {
      const { job_id } = await api.pull(backend, name);
      setPullName('');
      trackJob(job_id, 'pull', name, (snap) => {
        if (snap.state === 'success') {
          toast.success(`模型 ${name} 下載完成`);
        } else if (snap.state === 'cancelled') {
          toast.info(`已取消下載 ${name}`);
        } else {
          toast.error(`下載失敗：${snap.error ?? snap.message ?? '未知錯誤'}`);
        }
        refreshAfterAction({ delayed: false });
      });
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const handleStart = async (model: string) => {
    setBusyModel(model);
    try {
      const { job_id } = await api.start(backend, model);
      trackJob(job_id, 'start', model, (snap) => {
        setBusyModel(null);
        if (snap.state === 'success') {
          const effective = String(snap.result?.model ?? snap.model ?? model);
          // 接線：套用到問答 + 寫回 config.env（記住上次選擇）
          api
            .applyLlmRuntime()
            .then((r) => {
              if (r.applied) {
                const vlmNote = r.vlm_model ? `（圖片理解：${r.vlm_model}）` : '';
                toast.success(`已切換至 ${r.model}${vlmNote}${r.persisted ? '，並已記住' : ''}`);
                // 讓對話頁工具列的模型顯示即時跟上（useAppConfig 快取失效重抓）
                void refreshAppConfig();
              } else {
                toast.success(`模型 ${effective} 已啟動`);
              }
            })
            .catch(() => toast.success(`模型 ${effective} 已啟動`));
          const warning = snap.result?.warning;
          if (typeof warning === 'string' && warning) toast.warning(warning);
        } else if (snap.state !== 'cancelled') {
          toast.error(`啟動失敗：${snap.error ?? snap.message ?? '未知錯誤'}`);
        }
        refreshAfterAction();
      });
    } catch (e) {
      setBusyModel(null);
      toast.error(errText(e));
    }
  };

  const handleStop = async (model: string) => {
    setBusyModel(model);
    try {
      const { job_id } = await api.stop(backend, model);
      trackJob(job_id, 'stop', model, (snap) => {
        setBusyModel(null);
        if (snap.state === 'success') toast.success(`模型 ${model} 已停止`);
        else toast.error(`停止失敗：${snap.error ?? snap.message ?? '未知錯誤'}`);
        refreshAfterAction();
      });
    } catch (e) {
      setBusyModel(null);
      toast.error(errText(e));
    }
  };

  const handleDelete = async (model: string) => {
    setDeleting(null);
    try {
      await api.deleteModel(backend, model);
      toast.success(`模型 ${model} 已刪除`);
      void refreshModels(backend);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  /** 引擎加速開關：確認後套用（寫回 config.env + 立即重啟引擎）。 */
  const ENGINE_FAIL_TEXT: Record<string, string> = {
    engine_busy_permission:
      '無法接管目前的 Ollama（可能以系統管理員權限執行）。請關閉該 Ollama 後重試',
    engine_spawn_died: '引擎啟動後立即結束（可能埠被占用），請查看 ollama.log',
    engine_not_ready_after_restart: '引擎重啟後未在時限內就緒，請查看 ollama.log',
    engine_unavailable: '找不到可用的 Ollama（內附或已安裝版本）',
  };
  const handleEngineVulkan = async (vulkan: boolean) => {
    setConfirmVulkan(null);
    setEngineSwitching(true);
    try {
      const r = await api.setEngineEnv(vulkan);
      if (r.ok) {
        toast.success(
          vulkan
            ? '已切換為 iGPU（Vulkan）加速，模型將於下次提問時重新載入'
            : '已切換為自動模式（NVIDIA 走 CUDA，否則 CPU），模型將於下次提問時重新載入'
        );
      } else {
        toast.error(
          `引擎重啟未完成：${ENGINE_FAIL_TEXT[r.detail ?? ''] ?? r.detail ?? '未知原因'}（設定已保存）`
        );
      }
      setEngineEnvState(await api.engineEnv());
      refreshAfterAction();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setEngineSwitching(false);
    }
  };

  const runningNames = new Set(running.map(entryName));
  const activeJobList = Object.entries(jobs);
  const totalVram = resources?.gpus?.length
    ? resources.gpus.reduce((s, g) => s + (g.total_vram_bytes || 0), 0)
    : null;
  const freeVram = resources?.gpus?.length
    ? resources.gpus.reduce((s, g) => s + (g.free_vram_bytes || 0), 0)
    : null;

  /** 總開關切換（含確認）。 */
  const handleManagement = async (enabled: boolean) => {
    try {
      await api.setManagement(enabled);
      if (enabled) {
        toast.success('模型管理已啟用。建議重新啟動程式，讓引擎自動接管生效。');
        window.location.reload();
      } else {
        toast.success(
          '模型管理已停用。請由系統匣「設定（LLM 端點…）」自行設定端點，改完重啟程式。'
        );
        setEngineEnvState((prev) =>
          prev ? { ...prev, management_enabled: false } : prev
        );
      }
    } catch (e) {
      toast.error(errText(e));
    }
  };

  // ── 總開關停用狀態：整頁以說明卡取代（不打任何管理 API）──
  if (managementDisabled) {
    return (
      <div className="mx-auto max-w-5xl space-y-6 p-6">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Cpu className="h-6 w-6" /> 模型管理
        </h1>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">模型管理已停用</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              本程式目前<span className="text-foreground">不接管 AI 引擎</span>
              ——適用於自行運行 llama.cpp、vLLM、Ollama 等地端服務的環境。
            </p>
            <p>
              LLM 端點請由系統匣圖示 →「設定（LLM 端點…）」編輯
              <span className="font-mono"> config.env </span>
              的 <span className="font-mono">LLM_BASE_URL / LLM_MODEL</span>
              ，儲存後重新啟動程式即可接上你的服務。
            </p>
            <div className="pt-2">
              <Button onClick={() => void handleManagement(true)}>
                重新啟用模型管理
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Cpu className="h-6 w-6" /> 模型管理
        </h1>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void refreshStatus();
              void refreshModels(backend);
            }}
          >
            <RefreshCw className="mr-1 h-4 w-4" /> 重新整理
          </Button>
          {/* 總開關：自行運行 llama.cpp 等地端服務時，把整套管理功能關掉 */}
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={() => setConfirmDisable(true)}
          >
            停用模型管理…
          </Button>
        </div>
      </div>

      {/* 分頁（多後端才顯示;Windows 只有 Ollama） */}
      {backends.length > 1 && (
        <div className="flex gap-2">
          {backends.map((b) => (
            <Button
              key={b}
              variant={b === backend ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setBackend(b);
                void refreshModels(b);
              }}
            >
              {BACKEND_LABEL[b] ?? b}
            </Button>
          ))}
        </div>
      )}

      {/* 目前生效模型 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">目前生效模型（問答使用）</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {cfg === null ? (
            <div className="text-muted-foreground">載入中…</div>
          ) : cfg.ok && cfg.llm ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Badge>{BACKEND_LABEL[cfg.llm.backend] ?? cfg.llm.backend}</Badge>
                <span className="font-mono font-medium">{cfg.llm.model}</span>
                {cfg.llm.status === 'starting' && (
                  <Badge variant="outline" className="text-amber-600">啟動中</Badge>
                )}
                {cfg.llm.context_total != null && (
                  <span className="text-muted-foreground">
                    context {cfg.llm.context_total.toLocaleString()}（可輸入{' '}
                    {cfg.llm.max_input_tokens?.toLocaleString() ?? '—'}）
                  </span>
                )}
              </div>
              {(cfg.warnings ?? []).map((w, i) => (
                <div key={i} className="text-amber-600">
                  {w.code === 'model_on_cpu'
                    ? `⚠ 模型 ${w.model ?? ''} 正在 CPU 上推論，速度會明顯較慢`
                    : w.code === 'no_vision_model'
                      ? 'ℹ 目前沒有具視覺能力的模型，圖片理解功能自動停用'
                      : `⚠ ${w.code}`}
                </div>
              ))}
            </>
          ) : (
            <div className="text-muted-foreground">
              {cfg.reason === 'no_model_running'
                ? '目前沒有模型在運行——問答時會依 config.env 的設定自動載入，不影響使用。'
                : REASON_TEXT[cfg.reason ?? ''] ?? '狀態未知'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 資源 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">系統資源</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 text-sm sm:grid-cols-3">
          <div>
            <div className="mb-1 flex items-center gap-1 text-muted-foreground">
              <MemoryStick className="h-4 w-4" /> 記憶體
            </div>
            {resources ? (
              <>
                <Progress value={resources.ram.used_pct} className="h-2" />
                <div className="mt-1">
                  可用 {fmtBytes(resources.ram.available_bytes)} /{' '}
                  {fmtBytes(resources.ram.total_bytes)}
                </div>
              </>
            ) : (
              '—'
            )}
          </div>
          <div>
            <div className="mb-1 flex items-center gap-1 text-muted-foreground">
              <Cpu className="h-4 w-4" /> 顯示記憶體
            </div>
            {totalVram ? (
              <>
                <Progress
                  value={freeVram != null ? (1 - freeVram / totalVram) * 100 : 0}
                  className="h-2"
                />
                <div className="mt-1">
                  可用 {fmtBytes(freeVram)} / {fmtBytes(totalVram)}
                </div>
              </>
            ) : (
              <div className="text-muted-foreground">無法偵測（不影響使用）</div>
            )}
          </div>
          <div>
            <div className="mb-1 flex items-center gap-1 text-muted-foreground">
              <HardDrive className="h-4 w-4" /> 模型磁碟
            </div>
            {resources?.disk?.length ? (
              resources.disk
                .filter((d) => d.label !== 'vllm_models' || backends.includes('vllm'))
                .map((d) => (
                  <div key={d.label}>
                    {d.label === 'ollama_models' ? 'Ollama' : d.label}：可用{' '}
                    {fmtBytes(d.free_bytes as number | undefined)}
                  </div>
                ))
            ) : (
              '—'
            )}
          </div>
        </CardContent>
      </Card>

      {/* 下載新模型 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">下載新模型</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder={backend === 'ollama' ? '例：gemma3:12b、qwen3:8b' : '例：Qwen/Qwen3-8B'}
              value={pullName}
              onChange={(e) => setPullName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void handlePull()}
            />
            <Button onClick={() => void handlePull()} disabled={!pullName.trim()}>
              <Download className="mr-1 h-4 w-4" /> 下載
            </Button>
          </div>

          {/* 進行中的 job（下載/啟動/停止） */}
          {activeJobList.map(([jobId, j]) => (
            <div key={jobId} className="rounded-md border p-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="font-mono">{j.model}</span>
                  <span className="text-muted-foreground">
                    {j.kind === 'pull' ? '下載中' : j.kind === 'start' ? '啟動中' : '停止中'}
                    {j.snapshot?.message ? `：${j.snapshot.message}` : '…'}
                  </span>
                </div>
                {j.kind === 'pull' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => api.cancelJob(jobId).catch((e) => toast.error(errText(e)))}
                  >
                    <X className="mr-1 h-4 w-4" /> 取消
                  </Button>
                )}
              </div>
              {j.snapshot?.percent != null && (
                <div className="mt-2 flex items-center gap-3">
                  <Progress value={j.snapshot.percent} className="h-2 flex-1" />
                  <span className="whitespace-nowrap text-muted-foreground">
                    {Math.round(j.snapshot.percent)}%
                    {j.snapshot.speed_bps ? `・${fmtBytes(j.snapshot.speed_bps)}/s` : ''}
                    {j.snapshot.eta_sec ? `・${fmtEta(j.snapshot.eta_sec)}` : ''}
                  </span>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* 已安裝模型 */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="text-base">已安裝模型</CardTitle>
            {/* 引擎加速開關（引擎級設定，影響所有模型；僅內附引擎可用） */}
            {engineEnv?.engine_available && (
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <div className="text-sm">iGPU 加速（Vulkan）</div>
                  <div className="text-xs text-muted-foreground">
                    {engineEnv.gpus.length > 0
                      ? `偵測到：${engineEnv.gpus.join('、')}`
                      : '未偵測到顯示卡'}
                    {engineEnv.suggested_vulkan !== (engineEnv.OLLAMA_VULKAN === '1') &&
                      `（建議${engineEnv.suggested_vulkan ? '開啟' : '關閉'}）`}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    引擎：
                    {engineEnv.engine_source === 'user'
                      ? '使用者安裝版（模型沿用其既有目錄）'
                      : '內附版'}
                  </div>
                </div>
                {engineSwitching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Switch
                    checked={engineEnv.OLLAMA_VULKAN === '1'}
                    onCheckedChange={(next: boolean) => setConfirmVulkan(next)}
                  />
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loadingList ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> 載入中…
            </div>
          ) : models.length === 0 ? (
            <div className="py-6 text-sm text-muted-foreground">
              尚未安裝任何模型，請由上方下載（例：gemma3:12b）。
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>模型</TableHead>
                  <TableHead>大小</TableHead>
                  <TableHead>狀態</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {models.map((m) => {
                  const name = entryName(m);
                  const isRunning = runningNames.has(name);
                  const isCurrent = cfg?.ok && cfg.llm?.model === name;
                  return (
                    <TableRow key={name}>
                      <TableCell className="font-mono">{name}</TableCell>
                      <TableCell>{fmtBytes(m.size as number | undefined)}</TableCell>
                      <TableCell>
                        {isCurrent ? (
                          <Badge>使用中</Badge>
                        ) : isRunning ? (
                          <Badge variant="outline">運行中</Badge>
                        ) : (
                          <span className="text-muted-foreground">未載入</span>
                        )}
                      </TableCell>
                      <TableCell className="space-x-1 text-right">
                        {isRunning ? (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={busyModel === name}
                            onClick={() => void handleStop(name)}
                          >
                            <Square className="mr-1 h-3.5 w-3.5" /> 停止
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            disabled={busyModel === name}
                            onClick={() => void handleStart(name)}
                          >
                            <Play className="mr-1 h-3.5 w-3.5" /> 啟動
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          disabled={isRunning || busyModel === name}
                          onClick={() => setDeleting(name)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* 刪除確認 */}
      <AlertDialog open={deleting !== null} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>刪除模型</AlertDialogTitle>
            <AlertDialogDescription>
              確定要刪除 <span className="font-mono">{deleting}</span>{' '}
              嗎？模型檔案將從磁碟移除，需要時得重新下載。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleting && void handleDelete(deleting)}
            >
              刪除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 總開關停用確認 */}
      <AlertDialog open={confirmDisable} onOpenChange={setConfirmDisable}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>停用模型管理？</AlertDialogTitle>
            <AlertDialogDescription>
              停用後本程式將完全不接管 AI 引擎——適用於自行運行
              llama.cpp、vLLM 等地端服務的環境。LLM 端點改由系統匣
              「設定（LLM 端點…）」自行設定，設定完需重新啟動程式。
              隨時可回到本頁重新啟用。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmDisable(false);
                void handleManagement(false);
              }}
            >
              停用
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 引擎加速切換確認（會重啟引擎，進行中的回答會中斷） */}
      <AlertDialog
        open={confirmVulkan !== null}
        onOpenChange={(o) => !o && setConfirmVulkan(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmVulkan ? '切換為 iGPU（Vulkan）加速' : '切換為自動模式'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmVulkan
                ? '適用於 Intel / AMD 內顯機器。'
                : '由引擎自動偵測：有 NVIDIA 顯卡走 CUDA，否則使用 CPU。'}
              切換會立即重啟 AI 引擎——進行中的回答會中斷，已載入的模型將於
              下次提問時自動重新載入（需等待數十秒）。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                confirmVulkan !== null && void handleEngineVulkan(confirmVulkan)
              }
            >
              確認切換
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
